import React from 'react';
import { Card, Empty } from 'antd';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell
} from 'recharts';


const SourceCountryUsageChart = ({ data, showAllCountries }) => {
  let chartData = showAllCountries
    ? data.sort((a, b) => b.doc_count - a.doc_count)
    : data.sort((a, b) => b.doc_count - a.doc_count).slice(0, 5);

  if (!showAllCountries && chartData.length < 5) {
    const allCodes = chartData.map(d => d.key);
    for (let i = 0; i < data.length && chartData.length < 5; i++) {
      if (!allCodes.includes(data[i].key)) {
        chartData.push({ key: data[i].key, doc_count: 0 });
      }
    }
    while (chartData.length < 5) {
      chartData.push({ key: `--`, doc_count: 0 });
    }
  }

  const allZero = !chartData.some(d => d.doc_count > 0);
  if (!chartData.length || allZero) {
    return (
      <Card className="side-panel align-center" style={{ minHeight: '500px' }}>
        <Empty />
      </Card>
    );
  }

  const maxVal = Math.max(...chartData.map(d => d.doc_count), 0);
  let yMax = maxVal > 0 ? (maxVal <= 1000 ? Math.ceil(maxVal / 100) * 100 : Math.ceil(maxVal / 1000) * 1000) : 100;
  yMax = Math.max(yMax, 100);

  const countryNameMap = {
    AF: 'Afghanistan', AL: 'Albania', DZ: 'Algeria', AS: 'American Samoa', AD: 'Andorra', AO: 'Angola', AI: 'Anguilla', AQ: 'Antarctica', AG: 'Antigua and Barbuda', AR: 'Argentina',
    AM: 'Armenia', AW: 'Aruba', AU: 'Australia', AT: 'Austria', AZ: 'Azerbaijan', BS: 'Bahamas', BH: 'Bahrain', BD: 'Bangladesh', BB: 'Barbados', BY: 'Belarus',
    BE: 'Belgium', BZ: 'Belize', BJ: 'Benin', BM: 'Bermuda', BT: 'Bhutan', BO: 'Bolivia', BA: 'Bosnia and Herzegovina', BW: 'Botswana', BR: 'Brazil', IO: 'British Indian Ocean Territory',
    BN: 'Brunei', BG: 'Bulgaria', BF: 'Burkina Faso', BI: 'Burundi', KH: 'Cambodia', CM: 'Cameroon', CA: 'Canada', CV: 'Cape Verde', KY: 'Cayman Islands', CF: 'Central African Republic',
    TD: 'Chad', CL: 'Chile', CN: 'China', CO: 'Colombia', KM: 'Comoros', CD: 'Congo (Democratic Republic)', CG: 'Congo (Republic)', CR: 'Costa Rica', HR: 'Croatia', CU: 'Cuba',
    CY: 'Cyprus', CZ: 'Czech Republic', DK: 'Denmark', DJ: 'Djibouti', DM: 'Dominica', DO: 'Dominican Republic', EC: 'Ecuador', EG: 'Egypt', SV: 'El Salvador', GQ: 'Equatorial Guinea',
    ER: 'Eritrea', EE: 'Estonia', SZ: 'Eswatini', ET: 'Ethiopia', FJ: 'Fiji', FI: 'Finland', FR: 'France', GA: 'Gabon', GM: 'Gambia', GE: 'Georgia', DE: 'Germany', GH: 'Ghana',
    GR: 'Greece', GD: 'Grenada', GT: 'Guatemala', GN: 'Guinea', GW: 'Guinea-Bissau', GY: 'Guyana', HT: 'Haiti', HN: 'Honduras', HU: 'Hungary', IS: 'Iceland', IN: 'India',
    ID: 'Indonesia', IR: 'Iran', IQ: 'Iraq', IE: 'Ireland', IL: 'Israel', IT: 'Italy', JM: 'Jamaica', JP: 'Japan', JO: 'Jordan', KZ: 'Kazakhstan', KE: 'Kenya', KI: 'Kiribati',
    KP: 'North Korea', KR: 'South Korea', KW: 'Kuwait', KG: 'Kyrgyzstan', LA: 'Laos', LV: 'Latvia', LB: 'Lebanon', LS: 'Lesotho', LR: 'Liberia', LY: 'Libya', LI: 'Liechtenstein',
    LT: 'Lithuania', LU: 'Luxembourg', MG: 'Madagascar', MW: 'Malawi', MY: 'Malaysia', MV: 'Maldives', ML: 'Mali', MT: 'Malta', MH: 'Marshall Islands', MR: 'Mauritania',
    MU: 'Mauritius', MX: 'Mexico', FM: 'Micronesia', MD: 'Moldova', MC: 'Monaco', MN: 'Mongolia', ME: 'Montenegro', MA: 'Morocco', MZ: 'Mozambique', MM: 'Myanmar',
    NA: 'Namibia', NR: 'Nauru', NP: 'Nepal', NL: 'Netherlands', NZ: 'New Zealand', NI: 'Nicaragua', NE: 'Niger', NG: 'Nigeria', MK: 'North Macedonia', NO: 'Norway',
    OM: 'Oman', PK: 'Pakistan', PW: 'Palau', PS: 'Palestine', PA: 'Panama', PG: 'Papua New Guinea', PY: 'Paraguay', PE: 'Peru', PH: 'Philippines', PL: 'Poland',
    PT: 'Portugal', QA: 'Qatar', RO: 'Romania', RU: 'Russia', RW: 'Rwanda', KN: 'Saint Kitts and Nevis', LC: 'Saint Lucia', VC: 'Saint Vincent and the Grenadines',
    WS: 'Samoa', SM: 'San Marino', ST: 'Sao Tome and Principe', SA: 'Saudi Arabia', SN: 'Senegal', RS: 'Serbia', SC: 'Seychelles', SL: 'Sierra Leone', SG: 'Singapore',
    SK: 'Slovakia', SI: 'Slovenia', SB: 'Solomon Islands', SO: 'Somalia', ZA: 'South Africa', SS: 'South Sudan', ES: 'Spain', LK: 'Sri Lanka', SD: 'Sudan', SR: 'Suriname',
    SE: 'Sweden', CH: 'Switzerland', SY: 'Syria', TW: 'Taiwan', TJ: 'Tajikistan', TZ: 'Tanzania', TH: 'Thailand', TL: 'Timor-Leste', TG: 'Togo', TO: 'Tonga',
    TT: 'Trinidad and Tobago', TN: 'Tunisia', TR: 'Turkey', TM: 'Turkmenistan', TV: 'Tuvalu', UG: 'Uganda', UA: 'Ukraine', AE: 'United Arab Emirates', GB: 'United Kingdom',
    US: 'United States', UY: 'Uruguay', UZ: 'Uzbekistan', VU: 'Vanuatu', VE: 'Venezuela', VN: 'Vietnam', YE: 'Yemen', ZM: 'Zambia', ZW: 'Zimbabwe'
  };
  const getFullCountryName = (code) => countryNameMap[code] || code;

  const xTickFormatter = showAllCountries ? (value, index) => (index % 2 === 0 ? value : '') : getFullCountryName;
  const xInterval = showAllCountries ? 0 : 0;
  const barColors = ['#336699', '#418dbb', '#5db7c0', '#6bb7c7', '#bfe6e3', '#a3c9e2', '#7fa8be', '#b2d7e7'];

  return (
    <Card className="side-panel align-center" style={{ minHeight: '500px' }}>
      <div style={{ width: '100%', height: '100%', minHeight: '400px' }}>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 40, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="key" tickFormatter={xTickFormatter} label={{ value: 'Countries', position: 'insideBottom', offset: -10 }} tick={{ fontSize: 12 }} interval={xInterval} />
            <YAxis tickFormatter={v => v >= 1000 ? `${v/1000}k` : v} domain={[0, yMax]} label={{ value: 'No. of Users', angle: -90, position: 'insideLeft', offset: 10 }} width={80} tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value) => [value >= 1000 ? `${value}` : value, 'No. of Users']}
              labelFormatter={getFullCountryName}
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid #ccc', borderRadius: '4px', padding: '8px' }}
            />
            <Bar dataKey="doc_count" barSize={60}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default SourceCountryUsageChart;